<script setup>
/**
 * The internal composer.
 *
 * It carries a permanent lock note rather than a dismissible one: the whole
 * point of this thread is that nothing written in it reaches the customer, and a
 * trust cue that can be turned off is not a trust cue. The customer-facing
 * surface is the conversation, which is a different screen on purpose.
 *
 * Escape clears a reply or an edit rather than trapping focus in it (the audit's
 * §7.3 lesson), and an empty or unchanged body posts nothing at all.
 *
 * ⌘↵ / Ctrl↵ posts; a bare Enter stays a newline, because a note is a paragraph
 * and the accelerator is the thing that has to be learned, not the newline.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  // Prefilled body — an edit opens on the comment it is editing.
  initialContent: {
    type: String,
    default: '',
  },
  submitLabel: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  // A reply and an edit can both be abandoned; the root composer cannot.
  cancellable: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // The lock note belongs at the top of the thread once, not above every reply.
  showNote: {
    type: Boolean,
    default: true,
  },
  // The chat pane teaches the accelerator; an inline edit box does not need to.
  submitHint: {
    type: Boolean,
    default: false,
  },
  // A second, smaller reminder of where the note goes, for the pinned composer
  // whose lock line has scrolled out of the reader's attention. It is dropped
  // while the composer is disabled: a locked box already says so twice (the
  // pane's header marker and the lock line), and a third badge saying the same
  // thing is noise on the one state that needs the fewest words.
  noteBadge: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const MAX_LENGTH = 10000;

const { t } = useI18n();

const content = ref(props.initialContent);

watch(
  () => props.initialContent,
  value => {
    content.value = value;
  }
);

const canSubmit = computed(
  () => !props.disabled && Boolean(content.value.trim())
);

const onSubmit = () => {
  if (!canSubmit.value) return;
  emit('submit', content.value.trim());
  content.value = '';
};

const onCancel = () => {
  content.value = props.initialContent;
  emit('cancel');
};
</script>

<template>
  <form
    class="flex flex-col gap-2"
    data-testid="case-comment-composer"
    @submit.prevent="onSubmit"
  >
    <p
      v-if="showNote"
      class="inline-flex items-center gap-1.5 mb-0 text-xs text-n-slate-11"
      data-testid="case-composer-note"
    >
      <Icon icon="i-lucide-lock" class="size-3 shrink-0" />
      {{ t('SUPPORT_CASES.DETAIL.THREAD.INTERNAL_NOTE') }}
    </p>

    <TextArea
      v-model="content"
      auto-height
      :max-length="MAX_LENGTH"
      :autofocus="autofocus"
      :disabled="disabled"
      :placeholder="placeholder || t('SUPPORT_CASES.DETAIL.THREAD.PLACEHOLDER')"
      :aria-label="t('SUPPORT_CASES.DETAIL.THREAD.PLACEHOLDER')"
      @keydown.esc="onCancel"
      @keydown.meta.enter="onSubmit"
      @keydown.ctrl.enter="onSubmit"
    />

    <div class="flex flex-wrap items-center gap-2">
      <span
        v-if="noteBadge && !disabled"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-n-alpha-2 text-n-slate-11"
        data-testid="case-composer-note-badge"
      >
        <Icon icon="i-lucide-lock" class="size-3 shrink-0" />
        {{ t('SUPPORT_CASES.DETAIL.THREAD.NOTE_BADGE') }}
      </span>
      <span
        v-if="submitHint"
        class="text-xs text-n-slate-10 tabular-nums"
        data-testid="case-composer-hint"
      >
        {{ t('SUPPORT_CASES.DETAIL.THREAD.SUBMIT_HINT') }}
      </span>

      <div
        class="flex items-center gap-2 ltr:ml-auto rtl:mr-auto ltr:justify-end rtl:justify-start"
      >
        <Button
          v-if="cancellable"
          type="button"
          variant="ghost"
          color="slate"
          size="sm"
          :label="t('SUPPORT_CASES.DETAIL.THREAD.CANCEL')"
          data-testid="case-composer-cancel"
          @click="onCancel"
        />
        <Button
          type="submit"
          size="sm"
          :label="submitLabel || t('SUPPORT_CASES.DETAIL.THREAD.POST')"
          :disabled="!canSubmit"
          data-testid="case-composer-submit"
        />
      </div>
    </div>
  </form>
</template>
